import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, ConfirmDialog, DataTable, EmptyState, LoadingState, type Column } from "@agentnative/ui";
import { useAuthStore } from "../stores/auth";
import { apiErrorMessage } from "../lib/api/client";
import { listConnectedGrants, revokeConnectedGrant, revokeConnectedGrantOrg, type ConnectedGrant, type GrantOrganization } from "../lib/api/oauth";

const DROP_REASON: Record<string, string> = {
  membership_lost: "you left the organization",
  license_lost: "the license ended",
  org_policy_disabled: "the organization turned connectors off",
  user_revoked: "you revoked it",
};

function when(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : "never";
}

export function ConnectedAppsRoute() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<ConnectedGrant | null>(null);
  const [orgTarget, setOrgTarget] = useState<{ grant: ConnectedGrant; org: GrantOrganization } | null>(null);

  const grantsQuery = useQuery({ queryKey: ["oauth-grants"], queryFn: listConnectedGrants, enabled: !!user });
  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeConnectedGrant(id),
    onSuccess: () => {
      setTarget(null);
      queryClient.invalidateQueries({ queryKey: ["oauth-grants"] });
    },
  });

  const revokeOrgMutation = useMutation({
    mutationFn: (v: { grantId: string; orgId: string }) => revokeConnectedGrantOrg(v.grantId, v.orgId),
    onSuccess: () => {
      setOrgTarget(null);
      queryClient.invalidateQueries({ queryKey: ["oauth-grants"] });
    },
  });

  if (!user) {
    return <EmptyState title="Not signed in" description="Sign in to see the apps connected to your account." />;
  }

  const columns: Column<ConnectedGrant>[] = [
    {
      key: "app",
      label: "App",
      render: (g) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{g.client_name || "Unknown app"}</span>
            {g.unverified && <Badge variant="warning">Unverified</Badge>}
          </div>
          <div className="font-mono text-xs text-[var(--color-neutral-500)]">{g.client_id}</div>
        </div>
      ),
    },
    {
      key: "resource",
      label: "Has access to",
      render: (g) => (
        <div>
          <div>{g.resource_name || g.resource}</div>
          <div className="text-xs text-[var(--color-neutral-500)]">as you in:</div>
          <ul className="mt-1 space-y-1">
            {(g.organizations?.length ? g.organizations : [{ organization_id: g.organization_id, organization_name: g.organization_name, scopes: g.scopes, primary: true, revoked: false } as GrantOrganization]).map((o) => (
              <li key={o.organization_id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className={o.revoked ? "line-through" : undefined}>{o.organization_name || o.organization_id}</span>
                {o.primary && <Badge variant="info">Default</Badge>}
                <span className="font-mono text-xs text-[var(--color-neutral-500)]">{o.scopes.join(", ")}</span>
                {o.revoked ? (
                  <span className="text-xs text-[var(--color-neutral-500)]">removed: {DROP_REASON[o.revoked_reason ?? ""] ?? o.revoked_reason ?? "unknown reason"}</span>
                ) : (
                  !g.revoked && (
                    <Button variant="danger" size="sm" onClick={() => setOrgTarget({ grant: g, org: o })} aria-label={`Revoke ${o.organization_name || o.organization_id} for ${g.client_name || g.client_id}`}>
                      Revoke
                    </Button>
                  )
                )}
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    { key: "created", label: "Connected", render: (g) => when(g.created_at) },
    { key: "used", label: "Last used", render: (g) => when(g.last_used_at) },
    {
      key: "state",
      label: "Status",
      render: (g) =>
        g.revoked ? (
          <Badge variant="danger">Revoked</Badge>
        ) : new Date(g.expires_at) < new Date() ? (
          <Badge variant="warning">Expired</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        ),
    },
    {
      key: "actions",
      label: "",
      render: (g) =>
        g.revoked ? null : (
          <Button variant="danger" size="sm" onClick={() => setTarget(g)} aria-label={`Revoke ${g.client_name || g.client_id}`}>
            Revoke all
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Connected apps</h1>
      <p className="text-sm text-[var(--color-neutral-500)]">
        These are the apps, such as Claude connectors, that you allowed to act as you in one or more of your organizations, each with its own permissions. Each can
        only do what its permissions list, and never more than you can. Revoke an app to cut off its access right away; it
        has to ask you again to get it back. Automated agents that belong to an organization are managed under Agents.
      </p>

      {revokeOrgMutation.isError && (
        <Alert variant="danger" title="Could not revoke">
          {apiErrorMessage(revokeOrgMutation.error)}
        </Alert>
      )}
      {revokeMutation.isError && (
        <Alert variant="danger" title="Could not revoke">
          {apiErrorMessage(revokeMutation.error)}
        </Alert>
      )}

      {grantsQuery.isLoading ? (
        <LoadingState message="Loading connected apps..." />
      ) : grantsQuery.isError ? (
        <Alert variant="danger" title="Failed to load connected apps">
          {apiErrorMessage(grantsQuery.error)}
        </Alert>
      ) : (grantsQuery.data ?? []).length === 0 ? (
        <EmptyState title="No connected apps" description="When you authorize an app to use Agent Native, it appears here." />
      ) : (
        <Card padding="none">
          <DataTable<ConnectedGrant> data={grantsQuery.data!} columns={columns} emptyMessage="No connected apps." />
        </Card>
      )}

      <ConfirmDialog
        open={orgTarget !== null}
        onClose={() => setOrgTarget(null)}
        title="Revoke one organization"
        message={`${orgTarget?.grant.client_name || orgTarget?.grant.client_id || "This app"} will lose access to ${orgTarget?.org.organization_name || orgTarget?.org.organization_id || "this organization"} immediately. If it is the last organization of this connection, the whole connection ends.`}
        confirmText="Revoke organization"
        variant="danger"
        onConfirm={() => orgTarget && revokeOrgMutation.mutate({ grantId: orgTarget.grant.id, orgId: orgTarget.org.organization_id })}
      />
      <ConfirmDialog
        open={target !== null}
        onClose={() => setTarget(null)}
        title="Revoke access"
        message={`${target?.client_name || target?.client_id || "This app"} will lose access to ${target?.resource_name || target?.resource || "the app"} immediately.`}
        confirmText="Revoke access"
        variant="danger"
        onConfirm={() => target && revokeMutation.mutate(target.id)}
      />
    </div>
  );
}
